import {
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useRouteError,
} from "react-router-dom";

import { Home } from "@/routes/Home";
import {
  AuthRoot,
  LoginPage,
  LogoutPage,
  RegisterPage,
  ResetPasswordPage,
  VerifyPage,
} from "@/routes/auth";
import { AppRoot, DashboardPage, ProfilePage } from "@/routes/app";
import {
  DashboardPage as TenantDashboardPage,
  TenantRoot,
} from "@/routes/app/tenant";
import { BasePage } from "@/components/page";
import { TenantUserRoot, UserPage, UsersPage } from "@/routes/app/tenant/users";
import {
  TeamPage,
  TeamsPage,
  TenantTeamsRoot,
} from "@/routes/app/tenant/teams";
import { AgendaPage, TenantAgendasRoot } from "@/routes/app/tenant/agendas";
import {
  MeetingPage,
  MeetingsPage,
  TenantMeetingsRoot,
} from "@/routes/app/tenant/meetings";
import {
  AgendaTemplatePage,
  AgendaTemplatesPage,
  AgendaTemplatesRoot,
  LetterHeadPage,
  LetterHeadsPage,
  LetterHeadsRoot,
  SettingsPage,
  TenantSettingsRoot,
} from "@/routes/app/tenant/settings";
import {
  AgendaItemPage,
  AgendaItemsPage,
  TenantAgendaItemsRoot,
} from "@/routes/app/tenant/agendaItemsPages";
import {
  AgendaModulePage,
  AgendaModulesPage,
  TenantAgendaModulesRoot,
} from "@/routes/app/tenant/agendaModules";
import {
  DocumentPage,
  DocumentsPage,
  TenantDocumentsRoot,
} from "@/routes/app/tenant/documents";

import { NewSubscriptionPage, SubscriptionRoot } from "@/routes/subscription";
import { UnauthorizedPage } from "@/routes/Unauthorized";
import { BreadcrumbLink } from "@repo/ui/components/Base/Breadcrumb";
import type { AgendaItem, Meeting, Team, Tenant } from "@repo/types";
import ErrorPage from "@/routes/Error";
import { DebugPage } from "@/routes/app/Debug";
import { config } from "@/config";
import { useEffect } from "react";
import { captureException } from "@sentry/react";
import { CreateOrganizationPage, SetupRoot } from "@/routes/setup";
import { TermsAndConditionsPage } from "@/routes/TermsAndConditions";
import { MeetingRunPage } from "@/routes/app/tenant/meetings/meetingRun/MeetingRunPage";
import { IntegrationsRoot } from "@/routes/app/tenant/settings/IntegrationsRoot";
import { IntegrationLaserficheConfigPage } from "@/routes/app/tenant/settings/IntegrationLaserfiche";
import { IntegrationsPage } from "@/routes/app/tenant/settings/Integrations";
import { IntegrationLaserficheMultiConnectionsPage } from "@/routes/app/tenant/settings/IntegrationLaserficheMultiConnections";
import { MeetingRunSection } from "@/routes/app/tenant/meetings/meetingRun/MeetingRunSection";
import { DocumentSignPage } from "@/routes/app/tenant/documents/DocumentSign";
import { agendaItemRepo } from "@/repositories/agendaItemRepo";
import { firstValueFrom } from "rxjs";
import { tenantRepo } from "@/repositories/tenantRepo";
import { meetingRepo } from "@/repositories/meetingRepo";
import { teamRepo } from "@/repositories/teamRepo";

function SentryRouteErrorFallback() {
  const routeError = useRouteError();

  useEffect(() => {
    captureException(routeError, { level: "fatal" });
  }, [routeError]);

  return <ErrorPage />;
}

const router = () =>
  createBrowserRouter(
    createRoutesFromElements(
      <Route path="/" element={<BasePage />} errorElement={<ErrorPage />}>
        <Route index element={<Home />} errorElement={<ErrorPage />} />
        {config.env !== "production" && (
          <Route
            path="debug"
            element={<DebugPage />}
            errorElement={<SentryRouteErrorFallback />}
          />
        )}
        <Route
          path="error"
          element={<ErrorPage />}
          errorElement={<SentryRouteErrorFallback />}
        />
        <Route
          path="terms"
          element={<TermsAndConditionsPage />}
          errorElement={<SentryRouteErrorFallback />}
        />
        <Route
          path="auth"
          element={<AuthRoot />}
          errorElement={<SentryRouteErrorFallback />}
        >
          <Route
            path=""
            element={<LoginPage />}
            errorElement={<SentryRouteErrorFallback />}
          />
          <Route
            path="login"
            element={<LoginPage />}
            errorElement={<SentryRouteErrorFallback />}
          />
          <Route
            path="logout"
            element={<LogoutPage />}
            errorElement={<SentryRouteErrorFallback />}
          />
          <Route
            path="register"
            element={<RegisterPage />}
            errorElement={<SentryRouteErrorFallback />}
          />
          <Route
            path="verify"
            element={<VerifyPage />}
            errorElement={<SentryRouteErrorFallback />}
          />
          <Route
            path="resetPassword"
            element={<ResetPasswordPage />}
            errorElement={<SentryRouteErrorFallback />}
          />
        </Route>
        <Route
          path="setup"
          element={<SetupRoot />}
          errorElement={<SentryRouteErrorFallback />}
        >
          <Route path="firsttime" element={<CreateOrganizationPage />} />
        </Route>
        <Route
          path="subscription"
          element={<SubscriptionRoot />}
          errorElement={<SentryRouteErrorFallback />}
        >
          <Route path="" element={<NewSubscriptionPage />} />
        </Route>
        <Route
          path="unauthorized"
          element={<UnauthorizedPage />}
          errorElement={<SentryRouteErrorFallback />}
        />
        <Route
          path="/app"
          element={<AppRoot />}
          errorElement={<SentryRouteErrorFallback />}
        >
          <Route
            path=""
            element={<DashboardPage />}
            handle={{
              crumb: () => <BreadcrumbLink>Home</BreadcrumbLink>,
            }}
            errorElement={<SentryRouteErrorFallback />}
          />
          <Route
            path="profile"
            element={<ProfilePage />}
            errorElement={<SentryRouteErrorFallback />}
          />
          <Route
            path="tenant"
            element={<TenantRoot />}
            errorElement={<SentryRouteErrorFallback />}
          >
            <Route
              path=":tenantId"
              element={<TenantRoot />}
              loader={async ({ params }) =>
                (await firstValueFrom(
                  tenantRepo.getByFriendly(params.tenantId ?? "")
                ).catch(() => null)) ?? null
              }
              handle={{
                crumb: (data?: Tenant) => {
                  return (
                    <BreadcrumbLink>{data?.name ?? "Tenant"}</BreadcrumbLink>
                  );
                },
              }}
              errorElement={<SentryRouteErrorFallback />}
            >
              <Route
                path=""
                element={<TenantDashboardPage />}
                errorElement={<SentryRouteErrorFallback />}
              />
              <Route
                path="agendaItems"
                element={<TenantAgendaItemsRoot />}
                handle={{
                  crumb: () => <BreadcrumbLink>Agenda Items</BreadcrumbLink>,
                }}
              >
                <Route
                  path=""
                  element={<AgendaItemsPage />}
                  errorElement={<SentryRouteErrorFallback />}
                />
                <Route
                  path=":agendaItemId"
                  element={<AgendaItemPage />}
                  loader={async ({ params }) =>
                    (await firstValueFrom(
                      agendaItemRepo.get(
                        params.tenantId ?? "",
                        params.agendaItemId ?? ""
                      )
                    ).catch(() => null)) ?? null
                  }
                  handle={{
                    crumb: (data?: AgendaItem) => (
                      <BreadcrumbLink>
                        {data?.name ?? "Agenda Item"}
                      </BreadcrumbLink>
                    ),
                  }}
                  errorElement={<SentryRouteErrorFallback />}
                />
              </Route>
              <Route path="agendas" element={<TenantAgendasRoot />}>
                {/* <Route
                  path=""
                  element={<AgendasPage />}
                  handle={{
                    crumb: () => <BreadcrumbLink>Agendas</BreadcrumbLink>,
                  }}
                  errorElement={<SentryRouteErrorFallback />}
                /> */}
                <Route
                  path=":agendaId"
                  element={<AgendaPage />}
                  handle={{
                    crumb: () => <BreadcrumbLink>Agenda</BreadcrumbLink>,
                  }}
                  errorElement={<SentryRouteErrorFallback />}
                />
              </Route>
              <Route
                path="documents"
                element={<TenantDocumentsRoot />}
                handle={{
                  crumb: () => <BreadcrumbLink>Documents</BreadcrumbLink>,
                }}
              >
                <Route
                  path=""
                  element={<DocumentsPage />}
                  errorElement={<SentryRouteErrorFallback />}
                />
                <Route
                  path=":documentId"
                  element={<Outlet />}
                  handle={{
                    crumb: () => <BreadcrumbLink>Document</BreadcrumbLink>,
                  }}
                  errorElement={<SentryRouteErrorFallback />}
                >
                  <Route path="" element={<DocumentPage />} />
                  <Route
                    path="sign"
                    element={<DocumentSignPage />} // TODO
                    handle={{
                      crumb: () => <BreadcrumbLink>Sign</BreadcrumbLink>,
                    }}
                    errorElement={<SentryRouteErrorFallback />}
                  />
                </Route>
              </Route>
              <Route
                path="meetings"
                element={<TenantMeetingsRoot />}
                handle={{
                  crumb: () => <BreadcrumbLink>Meetings</BreadcrumbLink>,
                }}
                errorElement={<SentryRouteErrorFallback />}
              >
                <Route path="" element={<MeetingsPage />} />
                <Route
                  path=":meetingId"
                  element={<Outlet />}
                  loader={async ({ params }) =>
                    (await firstValueFrom(
                      meetingRepo.get(
                        params.tenantId ?? "",
                        params.meetingId ?? ""
                      )
                    ).catch(() => null)) ?? null
                  }
                  handle={{
                    crumb: (data?: Meeting) => (
                      <BreadcrumbLink>{data?.name ?? "Meeting"}</BreadcrumbLink>
                    ),
                  }}
                  errorElement={<SentryRouteErrorFallback />}
                >
                  <Route path="" element={<MeetingPage />} />
                  <Route
                    path="run"
                    element={<MeetingRunPage />}
                    handle={{
                      crumb: () => <BreadcrumbLink>Run Meeting</BreadcrumbLink>,
                    }}
                    errorElement={<SentryRouteErrorFallback />}
                  >
                    <Route
                      path=":meetingItem"
                      element={<MeetingRunSection />}
                    ></Route>
                  </Route>
                  <Route
                    path="agenda/:agendaId"
                    element={<Outlet />}
                    handle={{
                      crumb: () => <BreadcrumbLink>Agenda</BreadcrumbLink>,
                    }}
                    errorElement={<SentryRouteErrorFallback />}
                  >
                    <Route path="" element={<AgendaPage />} />
                    <Route
                      path="item/:agendaItemId"
                      element={<AgendaItemPage />}
                      loader={async ({ params }) =>
                        (await firstValueFrom(
                          agendaItemRepo.get(
                            params.tenantId ?? "",
                            params.agendaItemId ?? ""
                          )
                        ).catch(() => null)) ?? null
                      }
                      handle={{
                        crumb: (data?: AgendaItem) => (
                          <BreadcrumbLink>
                            {data?.name ?? "Agenda Item"}
                          </BreadcrumbLink>
                        ),
                      }}
                      errorElement={<SentryRouteErrorFallback />}
                    />
                  </Route>
                </Route>
              </Route>
              <Route
                path="teams"
                element={<TenantTeamsRoot />}
                handle={{
                  crumb: () => <BreadcrumbLink>Teams</BreadcrumbLink>,
                }}
                errorElement={<SentryRouteErrorFallback />}
              >
                <Route path="" element={<TeamsPage />} />
                <Route
                  path=":teamId"
                  element={<TeamPage />}
                  loader={async ({ params }) =>
                    (await firstValueFrom(
                      teamRepo.get(params.tenantId ?? "", params.teamId ?? "")
                    ).catch(() => null)) ?? null
                  }
                  handle={{
                    crumb: (data?: Team) => (
                      <BreadcrumbLink>{data?.name ?? "Team"}</BreadcrumbLink>
                    ),
                  }}
                  errorElement={<SentryRouteErrorFallback />}
                />
              </Route>
              <Route
                path="agendaModules"
                element={<TenantAgendaModulesRoot />}
                handle={{
                  crumb: () => <BreadcrumbLink>Agenda Sections</BreadcrumbLink>,
                }}
              >
                <Route
                  path=""
                  element={<AgendaModulesPage />}
                  errorElement={<SentryRouteErrorFallback />}
                />
                <Route
                  path=":agendaModuleId"
                  element={<AgendaModulePage />}
                  handle={{
                    crumb: () => (
                      <BreadcrumbLink>Agenda Section</BreadcrumbLink>
                    ),
                  }}
                  errorElement={<SentryRouteErrorFallback />}
                />
              </Route>
              <Route
                path="settings"
                element={<TenantSettingsRoot />}
                handle={{
                  crumb: () => <BreadcrumbLink>Settings</BreadcrumbLink>,
                }}
                errorElement={<SentryRouteErrorFallback />}
              >
                <Route path="" element={<SettingsPage />} />
                <Route
                  path="letterheads"
                  element={<LetterHeadsRoot />}
                  handle={{
                    crumb: () => <BreadcrumbLink>Letterheads</BreadcrumbLink>,
                  }}
                  errorElement={<SentryRouteErrorFallback />}
                >
                  <Route
                    path=""
                    element={<LetterHeadsPage />}
                    errorElement={<SentryRouteErrorFallback />}
                  />
                  <Route
                    path=":letterHeadId"
                    element={<LetterHeadPage />}
                    handle={{
                      crumb: () => <BreadcrumbLink>Letterhead</BreadcrumbLink>,
                    }}
                    errorElement={<SentryRouteErrorFallback />}
                  />
                </Route>
                <Route
                  path="agendaTemplates"
                  element={<AgendaTemplatesRoot />}
                  handle={{
                    crumb: () => (
                      <BreadcrumbLink>Agenda Templates</BreadcrumbLink>
                    ),
                  }}
                  errorElement={<SentryRouteErrorFallback />}
                >
                  <Route
                    path=""
                    element={<AgendaTemplatesPage />}
                    errorElement={<SentryRouteErrorFallback />}
                  />
                  <Route
                    path=":agendaTemplateId"
                    element={<AgendaTemplatePage />}
                    handle={{
                      crumb: () => (
                        <BreadcrumbLink>Agenda Template</BreadcrumbLink>
                      ),
                    }}
                  />
                </Route>
                <Route
                  path="integrations"
                  element={<IntegrationsRoot />}
                  handle={{
                    crumb: () => <BreadcrumbLink>Integrations</BreadcrumbLink>,
                  }}
                  errorElement={<SentryRouteErrorFallback />}
                >
                  <Route
                    path=""
                    element={<IntegrationsPage />}
                    errorElement={<SentryRouteErrorFallback />}
                  />
                  <Route
                    path="laserfiche"
                    handle={{
                      crumb: () => <BreadcrumbLink>Laserfiche</BreadcrumbLink>,
                    }}
                  >
                    <Route
                      path=""
                      element={<IntegrationLaserficheMultiConnectionsPage />}
                      errorElement={<SentryRouteErrorFallback />}
                    />
                    <Route
                      path=":connectionTeamId"
                      element={<IntegrationLaserficheConfigPage />}
                      handle={{
                        crumb: () => <BreadcrumbLink>Manage</BreadcrumbLink>,
                      }}
                      errorElement={<SentryRouteErrorFallback />}
                    />
                  </Route>
                </Route>
              </Route>
              <Route
                path="users"
                element={<TenantUserRoot />}
                handle={{
                  crumb: () => <BreadcrumbLink>Users</BreadcrumbLink>,
                }}
                errorElement={<SentryRouteErrorFallback />}
              >
                <Route path="" element={<UsersPage />} />
                <Route
                  path=":userId"
                  element={<UserPage />}
                  handle={{
                    crumb: () => <BreadcrumbLink>User</BreadcrumbLink>,
                  }}
                  errorElement={<SentryRouteErrorFallback />}
                />
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>
    )
  );

export const RouteProvider = () => {
  return <RouterProvider router={router()} />;
};
